import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ServiceSidebar = ({ service, relatedServices }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setShowSuccess(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    
    setTimeout(() => setShowSuccess(false), 5000);
  };

  return (
    <div className="space-y-6">
      {/* Service Highlights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-card rounded-lg border border-border p-6"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4">Service Highlights</h3>
        <div className="space-y-3">
          {service?.highlights?.map((highlight, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-5 h-5 bg-accent/20 rounded-full flex items-center justify-center">
                <Icon name="Check" size={12} className="text-accent" />
              </div>
              <span className="text-sm text-text-secondary">{highlight}</span>
            </div>
          ))}
        </div>
      </motion.div>
      {/* Pricing Tiers */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-card rounded-lg border border-border p-6"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4">Pricing Tiers</h3>
        <div className="space-y-4">
          {service?.pricingTiers?.map((tier, index) => (
            <div 
              key={index}
              className={`p-4 rounded-lg border ${
                tier?.popular 
                  ? 'border-accent bg-accent/5' :'border-border bg-muted/30'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-foreground">{tier?.name}</h4>
                {tier?.popular && (
                  <span className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded-full font-medium">
                    Popular
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold text-accent mb-2">{tier?.price}</div>
              <p className="text-sm text-text-secondary mb-3">{tier?.description}</p>
              <ul className="space-y-1">
                {tier?.features?.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="Check" size={12} className="text-accent flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </motion.div>
      {/* Quick Contact Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-card rounded-lg border border-border p-6"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4">Get Started Today</h3>
        
        {showSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={24} className="text-success" />
            </div>
            <h4 className="font-semibold text-foreground mb-2">Message Sent!</h4>
            <p className="text-sm text-text-secondary">We'll get back to you within 24 hours.</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              placeholder="Enter your name"
              required
            />
            
            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              required
            />
            
            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone"
            />
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Project Details
              </label>
              <textarea
                name="message"
                value={formData?.message}
                onChange={handleInputChange}
                placeholder="Tell us about your project..."
                rows={4}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                required
              />
            </div>
            
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              fullWidth
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        )}
      </motion.div>
      {/* Related Services */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-card rounded-lg border border-border p-6"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4">Related Services</h3>
        <div className="space-y-3">
          {relatedServices?.map((relatedService, index) => (
            <Link
              key={index}
              to="/services"
              className="block p-3 rounded-lg border border-border hover:border-accent/50 hover:bg-accent/5 transition-all duration-200 group"
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center group-hover:bg-accent/30 transition-colors duration-200">
                  <Icon name={relatedService?.icon} size={16} className="text-accent" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground group-hover:text-accent transition-colors duration-200">
                    {relatedService?.name}
                  </h4>
                  <p className="text-xs text-text-secondary">{relatedService?.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </motion.div>
      {/* Contact CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-gradient-to-r from-accent/20 to-accent/10 rounded-lg border border-accent/30 p-6 text-center"
      >
        <Icon name="Phone" size={24} className="text-accent mx-auto mb-3" />
        <h3 className="font-semibold text-foreground mb-2">Need Immediate Help?</h3>
        <p className="text-sm text-text-secondary mb-4">
          Speak directly with our experts
        </p>
        <Link to="/contact">
          <Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-accent-foreground">
            Contact Us Now
          </Button>
        </Link>
      </motion.div>
    </div>
  );
};

export default ServiceSidebar;